import { useCallback, useEffect, useState } from 'react';
import {
  applyTheme,
  nextPreference,
  readStoredPreference,
  resolveTheme,
  storePreference,
  type ResolvedTheme,
  type ThemePreference,
} from '@/lib/theme';

export interface UseThemeResult {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (next: ThemePreference) => void;
  cycle: () => void;
}

/**
 * Reads the stored preference, keeps the document in sync, and re-resolves when
 * the OS flips while the preference is "system".
 *
 * The initialiser only READS storage — the inline script in index.html has
 * already stamped data-theme before first paint, so there is nothing to write
 * here on mount and no flash to correct.
 */
export function useTheme(): UseThemeResult {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(preference));

  // Following the OS only means anything while the preference is "system";
  // an explicit choice must survive the student's phone switching to night mode.
  useEffect(() => {
    if (preference !== 'system') return;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setResolved(resolveTheme('system'));
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    setResolved(applyTheme(next));
    storePreference(next);
    setPreferenceState(next);
  }, []);

  const cycle = useCallback(
    () => setPreference(nextPreference(readStoredPreference())),
    [setPreference],
  );

  return { preference, resolved, setPreference, cycle };
}
